import React, { useMemo } from 'react';
import { Button } from 'primereact/button';
import { AXIcon } from '../icons';
import I18nWrapper from '../../i18n/i18n-wrapper';
import { AXButtonProps } from '../../../interfaces/components/AXButtonProps';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const AXButton = ({
  children,
  label,
  labelOptions,
  loading,
  loadingIcon = (
    <AXIcon
      icon="Loader"
      className="animate-spin mr-1"
      aria-label="loading"
      size="sm"
    />
  ),
  testid = label,
  icon,
  iconPos,
  iconSize,
  width,
  height,
  type = 'button',
  hide = false,
  to,
  restricted,
  disabled,
  onClick,
  ...rest
}: AXButtonProps) => {
  const { t } = useTranslation();
  const safeOnClick = typeof onClick === 'function' ? onClick : undefined;
  const buttonRender = useMemo(
    () => (
      <Button
        style={
          width !== undefined
            ? { width: width + 'rem', height: height + 'rem' }
            : undefined
        }
        data-qa={testid}
        data-testid={testid}
        loading={loading}
        loadingIcon={loadingIcon}
        unstyled
        type={type}
        disabled={disabled || restricted}
        aria-label={t(label ?? '')}
        onClick={safeOnClick}
        {...rest}
      >
        {children}
        {!loading && icon && (iconPos === 'left' || !iconPos) && (
          <span className="ax-btn__left">
            <AXIcon icon={icon} size={iconSize} />
          </span>
        )}
        {label && (
          <span className="ax-btn__label">
            <I18nWrapper translateKey={label} options={labelOptions} />
          </span>
        )}
        {!loading && !restricted && icon && iconPos === 'right' && (
          <span className="ax-btn__right">
            <AXIcon icon={icon} size={iconSize} />
          </span>
        )}
        {restricted && (
          <span className="ax-btn__right">
            <AXIcon icon="Lock" />
          </span>
        )}
      </Button>
    ),
    [
      children,
      disabled,
      height,
      icon,
      iconPos,
      iconSize,
      label,
      labelOptions,
      loading,
      loadingIcon,
      rest,
      restricted,
      safeOnClick,
      t,
      testid,
      type,
      width,
    ],
  );

  if (hide) return <></>;

  if (to && !disabled && !restricted) {
    return (
      <Link to={to} role="link">
        {buttonRender}
      </Link>
    );
  }

  return buttonRender;
};

export default AXButton;
